import React, { useRef, useState } from 'react';
import { Dropdown, Input } from 'tea-component';
import classNames from 'classnames';
import type {
  IDropdownSelectProps,
  IDropdownSelectOption,
  TDropdownValue,
} from './types';
import { getDisplayText, isOptionSelected, isCustomSelected } from './utils';
import './index.scss';

const DropdownSelect: React.FC<IDropdownSelectProps> = (props) => {
  const {
    options = [],
    customInput,
    value: initValue,
    defaultValue,
    onChange,
    validate,
    onValidationError,
    error: errorProp,
    className,
    boxClassName,
    overlayClassName,
    style,
    boxStyle,
    listWidth = 200,
    positionFixed,
    formatCustomDisplay,
  } = props || {};

  /** 下拉展开态 */
  const [open, setOpen] = useState<boolean>(false);
  /** 校验失败时保持下拉不关闭并展示输入框错误态 */
  const [internalError, setInternalError] = useState<boolean>(false);
  const showError = errorProp === true || internalError;
  /** 内部维护的选中值（非受控时使用） */
  const [value, setValue] = useState<TDropdownValue>(
    initValue ?? defaultValue ?? options?.[0]?.value
  );

  /** 受控时以 props 为准，保证选中态与展示与父组件一致 */
  const effectiveValue = initValue !== undefined ? initValue : value;

  /** 从 value 解析出的自定义数字，用于初始化 customVal/inputValue；非自定义时用 customInput.defaultCustomValue */
  const initCustomVal: number =
    typeof value === 'object' && (value as any)?.type === 'custom'
      ? (value as any)?.customValue ?? customInput?.defaultCustomValue ?? 0
      : customInput?.defaultCustomValue ?? 0;
  /** 自定义行已确认的数字（提交给父组件用） */
  const [customVal, setCustomVal] = useState(initCustomVal);
  /** 自定义输入框当前字符串（含输入中未确认的） */
  const [inputValue, setInputValue] = useState<string>(String(initCustomVal));

  const inputRef = useRef<HTMLInputElement | null>(null);
  /** 标记本次关闭是否由「点击预设选项」引起，避免关闭时误把自定义值回传覆盖选项 */
  const closingByOptionRef = useRef<boolean>(false);

  /** 打开下拉：重置“由选项关闭”标记与错误态 */
  const handleOpen = (): void => {
    closingByOptionRef.current = false;
    setInternalError(false);
    setOpen(true);
  };

  /**
   * 关闭下拉：若当前是自定义且非“点击选项”关闭，先校验再回传后关闭；校验失败则不关闭并展示错误态。
   * 若未输入有效自定义数值（输入为空或非法），则把自定义数值重置回默认值，再切回第一项（如「自动」）。
   */
  const handleClose = (): void => {
    if (closingByOptionRef.current) {
      closingByOptionRef.current = false;
      setOpen(false);
      return;
    }
    const isCustom =
      effectiveValue &&
      typeof effectiveValue === 'object' &&
      (effectiveValue as any).type === 'custom';
    if (isCustom) {
      const trimmed = String(inputValue ?? '').trim();
      const num = Number(inputValue);
      const hasValidInput =
        trimmed !== '' &&
        !isNaN(num) &&
        isFinite(num);
      if (!hasValidInput) {
        setInternalError(false);
        const resetVal =
          customInput?.defaultCustomValue !== undefined &&
          customInput?.defaultCustomValue !== null
            ? customInput.defaultCustomValue
            : 0;
        setCustomVal(resetVal);
        setInputValue(String(resetVal));
        setValueAndEmit(options?.[0]?.value ?? 'auto');
        setOpen(false);
        return;
      }
      const finalValue = num;
      const v: TDropdownValue = { type: 'custom', customValue: finalValue };
      const message = validate?.(v);
      if (typeof message === 'string' && message) {
        setInternalError(true);
        onValidationError?.(message);
        setOpen(true);
        return;
      }
      setInternalError(false);
      setValueAndEmit(v);
      setCustomVal(finalValue);
      setInputValue(String(finalValue));
    }
    setOpen(false);
  };

  /** 更新内部 value 并通知父组件 */
  const setValueAndEmit = (v: TDropdownValue): void => {
    setValue(v);
    onChange?.(v);
  };

  /** 点击预设选项：标记为“由选项关闭”，更新值并关闭；若从错误态切回自动，则重置自定义数值 */
  const handleSelectOption = (val: string | number): void => {
    closingByOptionRef.current = true;
    // 若当前存在自定义校验错误，且切回自动选项时，重置自定义数值为默认值，避免保留错误值
    if (val === 'auto' && customInput?.enabled) {
      const resetVal =
        customInput.defaultCustomValue !== undefined &&
          customInput.defaultCustomValue !== null
          ? customInput.defaultCustomValue
          : 0;
      setCustomVal(resetVal);
      setInputValue(String(resetVal));
    }
    setInternalError(false);
    setValueAndEmit(val);
    setOpen(false);
  };

  /** 确认自定义值并关闭（如按 Enter）：先校验，通过则回传并关闭；不通过则不关闭并展示错误态 */
  const confirmCustomAndClose = (): void => {
    const finalValue =
      !isNaN(Number(inputValue)) && isFinite(Number(inputValue))
        ? Number(inputValue)
        : customVal;
    const v: TDropdownValue = { type: 'custom', customValue: finalValue };
    const message = validate?.(v);
    if (typeof message === 'string' && message) {
      setInternalError(true);
      onValidationError?.(message);
      return;
    }
    setInternalError(false);
    setValueAndEmit(v);
    setCustomVal(finalValue);
    setInputValue(String(finalValue));
    setOpen(false);
  };

  /** 点击自定义行：先设为自定义并回传当前值（无有效值时用 defaultCustomValue），再聚焦输入框（不关闭） */
  const handleClickCustomRow = (): void => {
    let currentVal =
      !isNaN(Number(inputValue)) && isFinite(Number(inputValue))
        ? Number(inputValue)
        : customVal;
    const isAutoOrEmpty =
      effectiveValue === 'auto' ||
      effectiveValue === '' ||
      effectiveValue === null ||
      effectiveValue === undefined;
    if (
      isAutoOrEmpty &&
      (currentVal === 0 || currentVal === undefined) &&
      customInput?.defaultCustomValue !== undefined && customInput?.defaultCustomValue !== null
    ) {
      currentVal = customInput.defaultCustomValue;
      setCustomVal(currentVal);
      setInputValue(String(currentVal));
    }
    setValueAndEmit({ type: 'custom', customValue: currentVal });
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  /** 按钮展示文案 */
  const displayText = getDisplayText(
    effectiveValue,
    options,
    customInput,
    formatCustomDisplay,
  );

  return (
    <Dropdown
      className={classNames('dbl-dropdown-select', className)}
      boxClassName={classNames('dbl-dropdown-select-box', { 'dbl-dropdown-select-box--error': showError }, boxClassName)}
      overlayClassName={classNames(
        'dbl-dropdown-select-overlay',
        overlayClassName
      )}
      boxStyle={
        listWidth ? { ...(boxStyle || {}), width: `${listWidth}px` } : boxStyle
      }
      style={style}
      appearance="button"
      open={open}
      button={displayText}
      positionFixed={positionFixed}
      onOpen={handleOpen}
      onClose={handleClose}
      clickClose={false}
      updateOnChildrenChange
    >
      <div className="dbl-dropdown-select-content">
        {options?.map((opt: IDropdownSelectOption) => (
          <div
            key={String(opt?.value)}
            className={classNames('dbl-dropdown-select-item', {
              'dbl-dropdown-select-item--selected': isOptionSelected(
                opt,
                effectiveValue
              ),
            })}
            onClick={() => handleSelectOption(opt?.value)}
          >
            <div className="dbl-dropdown-select-item-label">{opt?.label}</div>
          </div>
        ))}
        {customInput?.enabled ? (
          <div
            className={classNames(
              'dbl-dropdown-select-item',
              'dbl-dropdown-select-item-custom',
              {
                'dbl-dropdown-select-item--selected': isCustomSelected(
                  effectiveValue
                ),
              }
            )}
            onClick={handleClickCustomRow}
          >
            <div>{customInput?.label || 'Custom Value'}</div>
            <div className={classNames('dbl-dropdown-select-custom-input', { 'dbl-dropdown-select-custom-input--error': showError })}>
              <Input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                value={inputValue}
                placeholder={customInput?.placeholder}
                onChange={(v) => {
                  const raw = v ?? '';
                  const filtered = raw.replace(/[^0-9\-.]/g, '');
                  const atMostOneMinusAtStart =
                    filtered.indexOf('-') === -1 ||
                    (filtered.indexOf('-') === 0 && filtered.indexOf('-', 1) === -1);
                  const atMostOneDot =
                    filtered.indexOf('.') === -1 ||
                    filtered.indexOf('.', filtered.indexOf('.') + 1) === -1;
                  if (!atMostOneMinusAtStart || !atMostOneDot) {
                    return;
                  }
                  setInputValue(filtered);

                  if (
                    filtered === '' ||
                    filtered === '-' ||
                    filtered === '.' ||
                    filtered === '-.'
                  ) {
                    return;
                  }
                  const numValue = Number(filtered);
                  if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
                    setCustomVal(numValue);
                  }
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e?.key === 'Enter') {
                    confirmCustomAndClose();
                  }
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Dropdown>
  );
};

export default DropdownSelect;
